import React, { FC, KeyboardEvent, useState } from "react";
import dayjs from "dayjs";
import Button, { ButtonIntentE, ButtonModeE } from "../../components/Button/Button";
import { H1, P, Span } from "../../components/Text/Text";
import { DATE_FORMAT, EXISTING_CAMPAIGN_WINDOW_DAYS } from "../../utils/constants";
import { clss } from "../../utils/helpers/styleHelpers";
import usePopup from "../../utils/hooks/usePopup";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { ExistingCampaignPromptPropsT, ExistingCampaignT } from "./models";

import styles from "./existingCampaignPrompt.module.scss";

type StepT = "default" | "choose" | "confirm";

const formatDate = (value: string | null | undefined) =>
  value ? dayjs(value).format(DATE_FORMAT) : "";

/**
 * spec-47 — opened by the campaign wizard (and the legacy one-step wizard) when POST UserPost answers
 * 302 with campaigns that already exist for this advertiser + brand from this client, with a broadcast
 * date in the last EXISTING_CAMPAIGN_WINDOW_DAYS days. One campaign, one primary action; the escape
 * hatch goes through a confirm step.
 *
 * Owns its whole content, including the title, because the title changes with the step — so the popup
 * config (index.tsx) passes no `header` / `footer`.
 *
 * Campaign names / advertiser / brand come from the server and are rendered in plain elements: the Text
 * components go through dangerouslySetInnerHTML and are for translatable UI strings only. Interpolated
 * t`…` values are passed as strings because t() drops falsy arguments (a 0 delivery count vanished).
 */
const ExistingCampaignPrompt: FC<ExistingCampaignPromptPropsT> = ({
  campaigns,
  onUseExisting,
  onCreateNew,
}) => {
  const { t } = useTranslate();
  const { removePopup } = usePopup();
  const [step, setStep] = useState<StepT>("default");
  const [selectedId, setSelectedId] = useState<number>(campaigns[0]?.Id);

  const selected: ExistingCampaignT | undefined =
    campaigns.find(({ Id }) => Id === selectedId) ?? campaigns[0];
  const others = campaigns.length - 1;

  const handleUseExisting = () => {
    if (selected) onUseExisting(selected.Id);
  };

  const handleCreateNew = () => {
    removePopup();
    onCreateNew();
  };

  const selectOnKey = (campaignId: number) => (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelectedId(campaignId);
    }
  };

  const primaryButton = (
    <Button onClick={handleUseExisting} withoutTranslate id="existing-campaign-use">
      {`${t`Add a delivery to campaign`} ${selected?.Id ?? ""}`}
    </Button>
  );

  if (step === "confirm") {
    return (
      <div className={styles.wrapper}>
        <H1>Create a separate campaign?</H1>
        <div className={styles.warning}>
          <P>
            Deliveries split across campaigns are not counted together, so the campaign's rating report
            will be incomplete.
          </P>
          <P>Choose this only if it really is a different campaign, for example, a different product.</P>
        </div>
        <div className={styles.actions}>
          {primaryButton}
          <Button
            mode={ButtonModeE.OUTLINED}
            intent={ButtonIntentE.CAMPAIGN}
            onClick={handleCreateNew}
            id="existing-campaign-create-new"
          >
            Yes, create a separate campaign
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <H1>This brand already has an active campaign</H1>
      <P className={styles.lead}>
        Add the new delivery to the existing campaign, so all of its versions are counted together in the
        rating reports.
      </P>

      {step === "default" && selected && (
        <div className={clss([styles.card, styles.chosen])}>
          <div className={styles.cardTitle}>
            <span className={styles.number}>{selected.Id}</span>
            <span>{selected.campaignName}</span>
          </div>
          <dl className={styles.facts}>
            <dt>
              <Span>Advertiser</Span>
            </dt>
            <dd>{selected.advertiserName}</dd>
            <dt>
              <Span>Brand</Span>
            </dt>
            <dd>{selected.brandName}</dd>
            <dt>
              <Span>Created</Span>
            </dt>
            <dd>{formatDate(selected.Created)}</dd>
            <dt>
              <Span>Deliveries</Span>
            </dt>
            <dd>{selected.DeliveryCount}</dd>
            {selected.LastBroadcastingDate && (
              <>
                <dt>
                  <Span>Broadcasting date</Span>
                </dt>
                <dd>{formatDate(selected.LastBroadcastingDate)}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {step === "choose" && (
        <>
          <p className={styles.pickerHeading}>
            {t`Matching campaigns for ${campaigns[0].advertiserName} / ${campaigns[0].brandName} with a broadcast date in the last ${String(
              EXISTING_CAMPAIGN_WINDOW_DAYS
            )} days`}
          </p>
          <ul className={styles.picker} role="radiogroup">
            {campaigns.map((campaign, index) => (
              <li
                key={campaign.Id}
                role="radio"
                aria-checked={campaign.Id === selectedId}
                tabIndex={0}
                className={clss([styles.row, campaign.Id === selectedId ? styles.rowSelected : ""])}
                onClick={() => setSelectedId(campaign.Id)}
                onKeyDown={selectOnKey(campaign.Id)}
              >
                <span className={styles.radio} />
                <div>
                  <div className={styles.rowTitle}>
                    <b>{campaign.Id}</b>
                    <span>{campaign.campaignName}</span>
                    {index === 0 && (
                      <span className={styles.chip}>
                        <Span>Most recent</Span>
                      </span>
                    )}
                  </div>
                  <div className={styles.rowMeta}>
                    {t`Created ${formatDate(campaign.Created)} · Deliveries: ${String(campaign.DeliveryCount)}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className={styles.actions}>
        <div className={styles.primaryRow}>
          {primaryButton}
          {step === "default" && others > 0 && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setStep("choose")}
              id="existing-campaign-choose"
            >
              {t`Not this one? ${String(others)} more matching campaigns`}
            </button>
          )}
        </div>
        <button
          type="button"
          className={styles.escapeHatch}
          onClick={() => setStep("confirm")}
          id="existing-campaign-different"
        >
          <Span>This is a different campaign – create a new one</Span>
        </button>
      </div>
    </div>
  );
};

export default ExistingCampaignPrompt;
